import { useState, useEffect } from 'react';
import { OnboardingStepProps, AISignal, FitLevel, ConfidenceLevel } from './types';

// Simulated AI extraction for demo
const SAMPLE_SIGNAL: AISignal = {
  roleFit: 'strong',
  levelAlignment: 'Likely Mid–Senior',
  strengths: [
    'Strong systems thinking demonstrated in past projects',
    'Clear communication style in written materials',
    'Evidence of ownership—led 3 major initiatives',
    'Good depth in distributed systems',
  ],
  risks: [
    'Limited evidence of cross-team collaboration',
    'No signals on mentorship or team leadership',
    'Frontend experience unclear',
  ],
  confidence: 'medium',
  reasoning: [
    'Resume shows 5 years of backend engineering experience at growth-stage companies',
    'GitHub contributions show consistent activity in infrastructure projects',
    'Communication artifacts suggest structured thinking',
  ],
  extractedSkills: ['Go', 'Kubernetes', 'PostgreSQL', 'System Design', 'API Design'],
};

const EXTRACTION_STEPS = [
  { label: 'Extracting skills', duration: 800 },
  { label: 'Estimating level alignment', duration: 600 },
  { label: 'Summarizing evidence', duration: 700 },
  { label: 'Identifying strengths', duration: 500 },
  { label: 'Detecting risks', duration: 600 },
  { label: 'Calculating confidence', duration: 400 },
];

function FitBadge({ fit }: { fit: FitLevel }) {
  const config = {
    strong: { bg: 'bg-green-100', text: 'text-green-700', label: 'Strong Fit' },
    mixed: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Mixed Signals' },
    weak: { bg: 'bg-red-100', text: 'text-red-700', label: 'Weak Fit' },
  };
  const { bg, text, label } = config[fit];
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${bg} ${text}`}>
      {label}
    </span>
  );
}

function ConfidenceBadge({ level }: { level: ConfidenceLevel }) {
  const config = {
    high: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', label: 'High Confidence' },
    medium: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', label: 'Medium Confidence' },
    low: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', label: 'Low Confidence' },
  };
  const { bg, border, text, label } = config[level];
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${bg} ${border} ${text}`}>
      {label}
    </span>
  );
}

export function StepAISignal({ onUpdate, onNext, onBack }: OnboardingStepProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isExtracting, setIsExtracting] = useState(true);
  const [signal, setSignal] = useState<AISignal | null>(null);

  useEffect(() => {
    if (!isExtracting) return;

    let timeout: ReturnType<typeof setTimeout>;
    
    const runStep = (stepIndex: number) => {
      if (stepIndex >= EXTRACTION_STEPS.length) {
        setIsExtracting(false);
        setSignal(SAMPLE_SIGNAL);
        onUpdate({ signal: SAMPLE_SIGNAL });
        return;
      }
      
      setCurrentStep(stepIndex);
      timeout = setTimeout(() => runStep(stepIndex + 1), EXTRACTION_STEPS[stepIndex].duration);
    };

    runStep(0);

    return () => clearTimeout(timeout);
  }, []);

  if (isExtracting) {
    return (
      <div className="animate-fade-in">
        <div className="text-center mb-16">
          <h1 className="text-3xl sm:text-4xl font-bold text-surface-900 mb-4">
            Extracting signal...
          </h1>
          <p className="text-lg text-surface-500 max-w-xl mx-auto">
            Turning messy evidence into structured insight.
          </p>
        </div>

        <div className="max-w-md mx-auto">
          <div className="space-y-4">
            {EXTRACTION_STEPS.map((step, index) => {
              const isComplete = index < currentStep;
              const isCurrent = index === currentStep;
              
              return (
                <div
                  key={step.label}
                  className={`
                    flex items-center gap-4 p-4 rounded-xl transition-all duration-300
                    ${isCurrent ? 'bg-brand-50 border-2 border-brand-200' : 'bg-surface-50'}
                  `}
                >
                  <div className={`
                    w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300
                    ${isComplete 
                      ? 'bg-green-500' 
                      : isCurrent 
                        ? 'bg-brand-500' 
                        : 'bg-surface-200'
                    }
                  `}>
                    {isComplete ? (
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    ) : isCurrent ? (
                      <svg className="w-4 h-4 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                    ) : (
                      <div className="w-2 h-2 rounded-full bg-surface-400" />
                    )}
                  </div>
                  <span className={`font-medium ${isCurrent ? 'text-brand-700' : isComplete ? 'text-surface-600' : 'text-surface-400'}`}>
                    {step.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  if (!signal) return null;

  return (
    <div className="animate-fade-in">
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-green-100 text-green-700 text-sm font-medium mb-6">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
          Analysis Complete
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold text-surface-900 mb-4">
          Here's what we found
        </h1>
        <p className="text-lg text-surface-500">
          Evidence extracted and structured for clear evaluation.
        </p>
      </div>

      <div className="max-w-2xl mx-auto space-y-6">
        {/* Top-level summary */}
        <div className="bg-white border-2 border-surface-200 rounded-2xl p-6">
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <FitBadge fit={signal.roleFit} />
            <ConfidenceBadge level={signal.confidence} />
          </div>
          
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <p className="text-sm font-medium text-surface-500 mb-1">Level Alignment</p>
              <p className="text-lg font-semibold text-surface-900">{signal.levelAlignment}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-surface-500 mb-1">Extracted Skills</p>
              <div className="flex flex-wrap gap-1.5">
                {signal.extractedSkills.map((skill) => (
                  <span key={skill} className="px-2 py-0.5 bg-surface-100 text-surface-700 text-sm rounded-md">
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Strengths */}
        <div className="bg-green-50 border border-green-200 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-7 h-7 rounded-lg bg-green-100 flex items-center justify-center">
              <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
            </div>
            <h3 className="font-semibold text-green-800">Strengths</h3>
          </div>
          <ul className="space-y-2">
            {signal.strengths.map((strength, i) => (
              <li key={i} className="flex items-start gap-2 text-green-900">
                <span className="text-green-500 mt-1">•</span>
                <span>{strength}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Risks / Unknowns */}
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-7 h-7 rounded-lg bg-amber-100 flex items-center justify-center">
              <svg className="w-4 h-4 text-amber-600" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
            </div>
            <h3 className="font-semibold text-amber-800">Risks & Unknowns</h3>
          </div>
          <ul className="space-y-2">
            {signal.risks.map((risk, i) => (
              <li key={i} className="flex items-start gap-2 text-amber-900">
                <span className="text-amber-500 mt-1">•</span>
                <span>{risk}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Reasoning */}
        <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-7 h-7 rounded-lg bg-surface-200 flex items-center justify-center">
              <svg className="w-4 h-4 text-surface-600" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
              </svg>
            </div>
            <h3 className="font-semibold text-surface-700">How we got here</h3>
          </div>
          <ul className="space-y-2">
            {signal.reasoning.map((reason, i) => (
              <li key={i} className="flex items-start gap-2 text-surface-600">
                <span className="text-surface-400 mt-1 font-mono text-xs">{i + 1}.</span>
                <span>{reason}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Confidence explanation */}
        <div className="text-center p-4 bg-brand-50/50 rounded-xl border border-brand-100">
          <p className="text-sm text-brand-700">
            <span className="font-medium">Why {signal.confidence} confidence?</span>
            {' '}
            {signal.confidence === 'high' && 'Multiple strong signals from different sources align.'}
            {signal.confidence === 'medium' && 'Good signal in some areas, but gaps remain.'}
            {signal.confidence === 'low' && 'Limited evidence—more signal needed before deciding.'}
          </p>
        </div>
      </div>

      <div className="mt-12 flex justify-center gap-4">
        <button onClick={onBack} className="btn btn-secondary text-base px-8 py-3.5">
          Back
        </button>
        <button
          onClick={onNext}
          className="btn bg-brand-600 text-white hover:bg-brand-700 shadow-lg shadow-brand-500/25 text-base px-10 py-3.5"
        >
          Make a Decision
          <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
    </div>
  );
}

